/**
 * BusinessDetail.Results.js
 * Componentes de visualización de resultados: investigación, guión, sitio generado.
 */
import React from "react";
import { Copy, ExternalLink, Loader2, RotateCcw, User } from "lucide-react";
import { ReportItem, ScriptBlock } from "./BusinessDetail.UI";
import { EditableScriptBlock, EditableBullets } from "./BusinessDetail.Editable";
import { authFetch, API } from "../utils/api";

// ─── ResearchResult ───────────────────────────────────────────────────────────
export function ResearchResult({ data }) {
  return (
    <div className="space-y-3 mb-3">
      {data.summary           && <ReportItem label="Resumen"                   value={data.summary} />}
      {data.value_proposition && <ReportItem label="Propuesta de valor"        value={data.value_proposition} />}
      {data.target_audience   && <ReportItem label="Público objetivo"          value={data.target_audience} />}
      {data.main_services?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Servicios</p>
          <div className="flex flex-wrap gap-1.5">
            {data.main_services.map((s) => (
              <span key={s} className="text-xs bg-white/[0.07] text-gray-300 px-2.5 py-1 rounded-full border border-white/[0.08]">{s}</span>
            ))}
          </div>
        </div>
      )}
      {data.pain_points              && <ReportItem label="Pain points"                  value={data.pain_points} />}
      {data.personalization_hooks    && <ReportItem label="Hooks de personalización"     value={data.personalization_hooks} />}
    </div>
  );
}

// ─── WebScriptResult ──────────────────────────────────────────────────────────
// All sections are inline-editable with debounced auto-save (no pencil icon).
// Changes are PATCHed to /api/businesses/:bizId/web-script and the UI stays
// in sync via onUpdate() bubbling the fresh document to the parent.
export function WebScriptResult({ data, onCopy, bizId, onUpdate }) {
  const patch = async (field, value) => {
    const res = await authFetch(`${API}/businesses/${bizId}/web-script`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ [field]: value }),
    });
    if (!res.ok) throw new Error(`PATCH failed: ${res.status}`);
    const fresh = await res.json();
    if (onUpdate) onUpdate(fresh);
    return fresh;
  };

  const copyAll = () => {
    const text = [
      `TITULAR: ${data.hero_headline}`,
      `SUBTITULAR: ${data.hero_subheadline}`,
      ``,
      `VALORES:`,
      ...(data.value_bullets || []).map(b => `• ${b}`),
      ``,
      `CTA PRINCIPAL: ${data.cta_primary}`,
      `CTA SECUNDARIO: ${data.cta_secondary}`,
      ``,
      `SOBRE NOSOTROS: ${data.about_headline}`,
      data.about_body,
      ``,
      `URGENCIA: ${data.urgency_text}`,
      ``,
      `TESTIMONIO: ${data.social_proof_text}`,
      ``,
      `META DESCRIPCIÓN: ${data.seo_meta_description}`,
    ].join("\n");
    onCopy(text);
  };

  return (
    <div className="space-y-3 mb-3">
      <div className="flex justify-between items-center">
        <p className="text-[10px] text-gray-600 italic">
          Clickeá cualquier texto para editarlo — se guarda solo.
        </p>
        <button onClick={copyAll} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors">
          <Copy size={11} /> Copiar todo
        </button>
      </div>

      <EditableScriptBlock
        label="Titular principal"
        value={data.hero_headline}
        placeholder="Titular principal del sitio…"
        onSave={(v) => patch("hero_headline", v)}
        onCopy={() => onCopy(data.hero_headline)}
        highlight
        testId="edit-hero-headline"
      />
      <EditableScriptBlock
        label="Subtitular"
        value={data.hero_subheadline}
        placeholder="Subtitular del hero…"
        onSave={(v) => patch("hero_subheadline", v)}
        onCopy={() => onCopy(data.hero_subheadline)}
        multiline
        testId="edit-hero-subheadline"
      />

      <EditableBullets
        values={data.value_bullets || []}
        onSave={(v) => patch("value_bullets", v)}
        testId="edit-value-bullets"
      />

      <div className="grid grid-cols-2 gap-2">
        <EditableScriptBlock
          label="CTA Principal"
          value={data.cta_primary}
          placeholder="CTA principal…"
          onSave={(v) => patch("cta_primary", v)}
          onCopy={() => onCopy(data.cta_primary)}
          small
          testId="edit-cta-primary"
        />
        <EditableScriptBlock
          label="CTA Secundario"
          value={data.cta_secondary}
          placeholder="CTA secundario…"
          onSave={(v) => patch("cta_secondary", v)}
          onCopy={() => onCopy(data.cta_secondary)}
          small
          testId="edit-cta-secondary"
        />
      </div>

      <EditableScriptBlock
        label="Sobre nosotros — Titular"
        value={data.about_headline}
        placeholder="Titular de la sección Sobre nosotros…"
        onSave={(v) => patch("about_headline", v)}
        onCopy={() => onCopy(data.about_headline)}
        testId="edit-about-headline"
      />
      <EditableScriptBlock
        label="Sobre nosotros — Cuerpo"
        value={data.about_body}
        placeholder="Descripción de la sección Sobre nosotros…"
        onSave={(v) => patch("about_body", v)}
        onCopy={() => onCopy(data.about_body)}
        multiline
        testId="edit-about-body"
      />

      <EditableScriptBlock
        label="Testimonio sugerido"
        value={data.social_proof_text}
        placeholder="Cita o testimonio corto…"
        onSave={(v) => patch("social_proof_text", v)}
        onCopy={() => onCopy(data.social_proof_text)}
        multiline
        testId="edit-social-proof"
      />

      <div className="bg-amber-900/20 border border-amber-700/30 rounded-xl p-3">
        <div className="flex justify-between items-center mb-1.5">
          <p className="text-[10px] font-bold text-amber-500 uppercase tracking-wide">Línea de urgencia</p>
          <button
            onClick={() => onCopy(data.urgency_text)}
            className="text-amber-600 hover:text-amber-400 transition-colors"
          >
            <Copy size={12} />
          </button>
        </div>
        <EditableScriptBlock
          label=""
          value={data.urgency_text}
          placeholder="Mensaje de urgencia…"
          onSave={(v) => patch("urgency_text", v)}
          testId="edit-urgency"
        />
      </div>

      <EditableScriptBlock
        label="Meta descripción SEO"
        value={data.seo_meta_description}
        placeholder="Meta descripción para buscadores…"
        onSave={(v) => patch("seo_meta_description", v)}
        onCopy={() => onCopy(data.seo_meta_description)}
        multiline
        testId="edit-seo-meta"
      />
    </div>
  );
}

// ─── SiteResult ───────────────────────────────────────────────────────────────
export function SiteResult({ site, previewUrl, adminPreviewUrl, clientToken, onCopy, onResetCountdown, resettingCountdown }) {
  return (
    <div className="space-y-3 mb-3">
      <div className="bg-white/[0.05] border border-white/[0.08] rounded-xl p-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">URL del Cliente</p>
        <div className="flex items-center gap-2">
          <code className="text-xs text-[#ffc700] flex-1 break-all">{previewUrl}</code>
          <button
            data-testid="copy-preview-url"
            onClick={() => onCopy(previewUrl)}
            className="flex-shrink-0 p-1.5 bg-white/[0.07] border border-white/[0.08] rounded-lg hover:bg-white/[0.12] transition-colors"
          >
            <Copy size={13} className="text-gray-400" />
          </button>
        </div>
      </div>

      <div className="flex gap-2">
        <a
          data-testid="open-preview-link"
          href={adminPreviewUrl || previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-[#FF4F00] text-white rounded-xl text-sm font-semibold hover:bg-orange-600 transition-colors"
        >
          <ExternalLink size={14} /> Vista previa
        </a>
        {clientToken && (
          <a
            data-testid="open-client-dashboard-link"
            href={`/client/${clientToken}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-white/[0.1] text-gray-200 rounded-xl text-sm font-semibold hover:bg-white/[0.15] transition-colors border border-white/[0.08]"
          >
            <User size={14} /> Panel del cliente
          </a>
        )}
      </div>

      <button
        data-testid="reset-countdown-btn"
        onClick={onResetCountdown}
        disabled={resettingCountdown}
        className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-semibold text-gray-400 hover:text-white border border-white/[0.07] hover:border-white/20 bg-white/[0.03] hover:bg-white/[0.07] transition-all disabled:opacity-50"
      >
        {resettingCountdown ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
        {resettingCountdown ? "Reiniciando..." : "Reiniciar contador de oferta"}
      </button>
    </div>
  );
}
